import React, { useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Check, Sparkles, ArrowLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import OutlineUploader from "@/components/wizard/OutlineUploader";
import ChapterPlanTable from "@/components/wizard/ChapterPlanTable";
import GenerationProgress from "./GenerationProgress";

const DEFAULT_PLAN = [
    { title: "", hours: 2 },
    { title: "", hours: 2 },
    { title: "", hours: 2 },
];

export default function OutlineGenerateDialog({
    open,
    onOpenChange,
    programId,
    existingOutline,
    hasExistingChapters,
    onCompleted,
}) {
    const [step, setStep] = useState("outline");
    const [outline, setOutline] = useState(existingOutline || null);
    const [plan, setPlan] = useState(DEFAULT_PLAN);
    const [lessonsPerHour, setLessonsPerHour] = useState(3);
    const [questionsPerQuiz, setQuestionsPerQuiz] = useState(10);
    const [replaceExisting, setReplaceExisting] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [jobId, setJobId] = useState(null);
    const [genResult, setGenResult] = useState(null);

    const reset = () => {
        setStep("outline");
        setOutline(existingOutline || null);
        setPlan(DEFAULT_PLAN);
        setReplaceExisting(false);
        setSubmitting(false);
        setJobId(null);
        setGenResult(null);
    };

    const close = (v) => {
        if (!v) reset();
        onOpenChange(v);
    };

    const runGeneration = async () => {
        const cleanPlan = plan
            .filter((c) => c.title.trim())
            .map((c) => ({ title: c.title.trim(), hours: Number(c.hours) || 1 }));
        if (cleanPlan.length === 0) {
            toast.error("Add at least one chapter to the plan");
            return;
        }
        setStep("generate");
        setSubmitting(true);
        setJobId(null);
        setGenResult(null);
        try {
            const { data } = await api.post(
                `/programs/${programId}/generate-from-outline`,
                {
                    chapters: cleanPlan,
                    lessons_per_hour: lessonsPerHour,
                    questions_per_quiz: questionsPerQuiz,
                    replace_existing: replaceExisting,
                },
            );
            setJobId(data.job_id);
        } catch (e) {
            const errorText = e?.response?.data?.detail || "Could not start generation";
            toast.error(errorText);
            setGenResult({ failed: true, error: errorText });
            setSubmitting(false);
        }
    };

    const handleComplete = (job) => {
        setSubmitting(false);
        setGenResult(job);
        if (job.failures > 0) {
            toast.warning(
                `${job.chapters_created}/${job.total} chapters generated. ${job.failures} failed.`,
            );
        } else {
            toast.success(
                `Generated ${job.chapters_created} chapters and ${job.lessons_created} lessons.`,
            );
        }
        onCompleted?.();
    };

    const handleError = (job) => {
        setSubmitting(false);
        const errorText = typeof job?.error === "string" ? job.error : "Generation failed";
        setGenResult({ ...(job || {}), failed: true, error: errorText });
        toast.error(errorText);
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent
                className="max-w-3xl max-h-[90vh] overflow-y-auto"
                data-testid="outline-generate-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-display">
                        Generate curriculum from outline
                    </DialogTitle>
                    <DialogDescription>
                        Upload a program outline, then define chapters with target hours. AI will
                        write detailed lessons and a chapter quiz.
                    </DialogDescription>
                </DialogHeader>

                {step === "outline" && (
                    <div className="space-y-5">
                        <OutlineUploader
                            programId={programId}
                            onUploaded={setOutline}
                            initialPreview={outline?.preview || existingOutline?.preview}
                            initialFilename={outline?.filename || existingOutline?.filename}
                        />
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => close(false)}>
                                Cancel
                            </Button>
                            <Button
                                onClick={() => setStep("chapters")}
                                disabled={!outline && !existingOutline}
                                className="bg-primary hover:bg-primary/90"
                                data-testid="og-next-from-outline"
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                )}

                {step === "chapters" && (
                    <div className="space-y-5">
                        <ChapterPlanTable
                            chapters={plan}
                            onChange={setPlan}
                            lessonsPerHour={lessonsPerHour}
                        />
                        <div className="grid grid-cols-2 gap-4 pt-2 border-t border-border">
                            <div>
                                <Label>Lessons per hour</Label>
                                <Select
                                    value={String(lessonsPerHour)}
                                    onValueChange={(v) => setLessonsPerHour(Number(v))}
                                >
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="2">2 (longer lessons)</SelectItem>
                                        <SelectItem value="3">3 (default)</SelectItem>
                                        <SelectItem value="4">4 (shorter lessons)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>MCQs per chapter quiz</Label>
                                <Select
                                    value={String(questionsPerQuiz)}
                                    onValueChange={(v) => setQuestionsPerQuiz(Number(v))}
                                >
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="5">5</SelectItem>
                                        <SelectItem value="10">10 (default · pass 7/10)</SelectItem>
                                        <SelectItem value="15">15</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {hasExistingChapters && (
                            <label className="flex items-start gap-2 text-sm p-3 border border-amber-200 bg-amber-50 rounded-sm cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={replaceExisting}
                                    onChange={(e) => setReplaceExisting(e.target.checked)}
                                    className="mt-0.5"
                                    data-testid="og-replace-existing"
                                />
                                <div>
                                    <div className="font-medium text-amber-900 inline-flex items-center gap-1">
                                        <AlertTriangle className="h-3 w-3" />
                                        Replace all existing chapters
                                    </div>
                                    <div className="text-xs text-amber-800">
                                        Wipe current chapters, lessons and quizzes before generating
                                        new ones. Also resets student progress for this program.
                                    </div>
                                </div>
                            </label>
                        )}

                        <div className="flex justify-between gap-2 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setStep("outline")}
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Back
                            </Button>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => close(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={runGeneration}
                                    disabled={submitting}
                                    className="bg-primary hover:bg-primary/90"
                                    data-testid="og-generate-btn"
                                >
                                    <Sparkles className="h-4 w-4 mr-2" />
                                    Generate
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {step === "generate" && (
                    <div className="space-y-5 py-4">
                        {jobId && submitting && (
                            <GenerationProgress
                                jobId={jobId}
                                onComplete={handleComplete}
                                onError={handleError}
                            />
                        )}
                        {!submitting && genResult && !genResult.failed && (
                            <div className="text-center py-4">
                                <div className="h-14 w-14 rounded-full bg-accent/20 text-accent flex items-center justify-center mx-auto mb-3">
                                    <Check className="h-7 w-7" />
                                </div>
                                <div className="font-display text-2xl text-primary">
                                    Curriculum updated
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">
                                    {genResult.chapters_created} chapter
                                    {genResult.chapters_created === 1 ? "" : "s"} and{" "}
                                    {genResult.lessons_created} lesson
                                    {genResult.lessons_created === 1 ? "" : "s"} created.
                                    {genResult.failures > 0
                                        ? ` ${genResult.failures} chapter(s) failed and can be retried.`
                                        : ""}
                                </p>
                                <div className="flex gap-2 justify-center mt-6">
                                    <Button
                                        onClick={() => close(false)}
                                        className="bg-primary hover:bg-primary/90"
                                    >
                                        Review in editor
                                    </Button>
                                </div>
                            </div>
                        )}
                        {!submitting && genResult?.failed && (
                            <div className="py-4">
                                <div className="text-center">
                                    <div className="font-display text-lg text-destructive">
                                        Generation failed
                                    </div>
                                    {genResult.error && typeof genResult.error === "string" && (
                                        <p className="text-sm text-foreground/85 mt-2 max-w-md mx-auto">
                                            {genResult.error}
                                        </p>
                                    )}
                                    {genResult.error &&
                                        /Budget has been exceeded|Max budget/i.test(
                                            genResult.error,
                                        ) && (
                                            <div className="mt-3 text-sm text-foreground/85 bg-amber-50 border border-amber-200 rounded-sm p-3 text-left max-w-md mx-auto">
                                                Your Emergent LLM key budget is exhausted. Open
                                                your profile →{" "}
                                                <span className="font-medium">
                                                    Universal Key → Add Balance
                                                </span>{" "}
                                                (or enable auto top-up), then retry.
                                            </div>
                                        )}
                                    <p className="text-sm text-muted-foreground mt-3">
                                        Try again with fewer chapters, or close this dialog and
                                        add content manually.
                                    </p>
                                    <div className="flex gap-2 justify-center mt-6">
                                        <Button
                                            variant="outline"
                                            onClick={() => setStep("chapters")}
                                        >
                                            Back to plan
                                        </Button>
                                        <Button onClick={() => close(false)}>Close</Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
